import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useLazyQuery } from '@apollo/client';
import { CloseOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { Form } from '@ant-design/compatible';
import { Row, Col, Button, Tooltip, Radio, Checkbox, InputNumber, message, Select } from 'antd';
import classNames from 'classnames';
import modal from '~hocs/modal';
import useStyles from './style';
import { UPDATE_PAYMENT_PLAN_INSTALLMENTS, QUERY_COUNTRIES } from '../queries';
import TierDefineControl from './tier-define-control';
import { getSafeHtml } from 'common/helpers/safeHtml';

const { Option } = Select;

const DEFAULT_TENANCY_TIERS = [
  {
    fromTenancy: 0,
    toTenancy: 18,
  },
  {
    fromTenancy: 18,
    toTenancy: 39,
  },
  {
    fromTenancy: 39,
    toTenancy: null,
  },
];

const NO_TENANCY_TIERS = [
  {
    fromTenancy: 0,
    toTenancy: null,
  },
];

const YES = 'YES';
const NO = 'NO';
const FULL_PAYMENT = 'FULL_PAYMENT';
const RENTAL_INSTALMENTS = 'RENTAL_INSTALMENTS';
const ALL_AT_ONCE = 'ALL_AT_ONCE';
const QUARTERLY = 'QUARTERLY';
const MONTHLY = 'MONTHLY';
const WEEKLY = 'WEEKLY';
const WEEKS = 'WEEKS';
const MONTHS = 'MONTHS';

const RentPaymentSettingModal = ({
  form,
  paymentPlanId,
  instalments,
  onClose,
  getPaymentPlan,
  isMustPayDespositFee,
  properties,
  landlord,
  paymentOnline,
}) => {
  console.log('sssseeee', paymentOnline);
  const {
    getFieldDecorator,
    getFieldValue,
    validateFields,
    resetFields,
    setFieldsValue,
    getFieldsValue,
  } = form;
  // 选中的property的国家
  const defaultCountry = properties.node?.city?.country?.name;

  const { t } = useTranslation(['landlord', 'cms']);
  const classes = useStyles();

  const [tenancyLengthTiers, setTenancyLengthTiers] = useState(instalments?.length > 1 ? YES : NO);

  const [chooseCountries, setChooseCountries] = useState([]);
  const [tenancyInstalments, setTenancyInstalments] = useState(
    instalments?.length > 0 ? instalments : NO_TENANCY_TIERS,
  );
  const wrapperRef = useRef(null);

  const [updateRentPayment, { loading }] = useMutation(UPDATE_PAYMENT_PLAN_INSTALLMENTS, {
    onCompleted: () => {
      onClose();
      getPaymentPlan({
        id: paymentPlanId,
      });
    },
    onError: err => {
      message.error(err.message);
    },
  });
  const [queryCountry] = useLazyQuery(QUERY_COUNTRIES, {
    fetchPolicy: 'no-cache',
    onCompleted: res => {
      let countries;
      if (res?.search?.edges?.length) {
        countries = res?.search?.edges?.map(item => item?.node?.name);
      } else {
        countries = [];
      }
      setChooseCountries(countries);
    },
  });
  const getTierRenderText = useCallback(tier => {
    const { fromTenancy, toTenancy } = tier;

    if (fromTenancy === 0 && toTenancy) {
      return `&le; ${toTenancy} ${
        toTenancy > 1
          ? t('landlord.payment.long_tail.rent_payment.setting_modal.weeks')
          : t('landlord.payment.long_tail.rent_payment.setting_modal.week')
      }`;
    } else if (fromTenancy && toTenancy) {
      return `${fromTenancy} &lt; ${
        toTenancy > 1
          ? t('landlord.payment.long_tail.rent_payment.setting_modal.weeks')
          : t('landlord.payment.long_tail.rent_payment.setting_modal.week')
      } &le; ${toTenancy}`;
    } else if (fromTenancy && !toTenancy) {
      return `&gt; ${fromTenancy} ${
        fromTenancy > 1
          ? t('landlord.payment.long_tail.rent_payment.setting_modal.weeks')
          : t('landlord.payment.long_tail.rent_payment.setting_modal.week')
      }`;
    }
  });
  const onSearch = searchText => {
    queryCountry({
      variables: {
        query: searchText,
      },
    });
  };
  const getPaymentOptionsParams = useCallback(values => {
    const { installments = [] } = values;

    return installments.map(option => {
      option.fullPayment = option?.payment?.includes(FULL_PAYMENT) ? true : false;
      option.rentalInstallment = option?.payment?.includes(RENTAL_INSTALMENTS) ? true : false;

      if (option.rentalInstallment) {
        option.payRemainderAtOnce = option?.payLaterToYouDirectly?.includes(ALL_AT_ONCE);

        option.payQuarterly = option?.payLaterToYouDirectly?.includes(QUARTERLY);
        option.payMonthly = option?.payLaterToYouDirectly?.includes(MONTHLY);
        option.payWeekly = option?.payLaterToYouDirectly?.includes(WEEKLY);
        option.requireGuarantor = option.requireGuarantor === YES;
        if (option?.guarantorLocation === 'special') {
          option.country = option?.guarantorCountry;
        } else {
          option.country = null;
        }
        if (option?.guarantorLocation) {
          delete option?.guarantorLocation;
          delete option?.guarantorCountry;
        }
      } else {
        option.payRemainderAtOnce =
          option.payQuarterly =
          option.payMonthly =
          option.payWeekly =
          option.requireGuarantor =
          option.payNowAmount =
            null;
        option.tenancyUnit = WEEKS;
      }

      delete option.payment;

      delete option.payLaterToYouDirectly;

      return option;
    });
  });

  const paymentOptionInitialValues = useMemo(() => {
    return tenancyInstalments.map(tier => {
      const values = {
        payment: [],
        payLaterToYouDirectly: [ALL_AT_ONCE],
      };
      // no payment 默认付款方式full payment
      if (!paymentOnline) {
        values.payment.push(FULL_PAYMENT);
      }
      if (tier.fullPayment) {
        values.payment.push(FULL_PAYMENT);
      }
      if (tier.payNowAmount || typeof tier.requireGuarantor == 'boolean') {
        values.payment.push(RENTAL_INSTALMENTS);
      }

      if (tier.payQuarterly) {
        values.payLaterToYouDirectly.push(QUARTERLY);
      }
      if (tier.payMonthly) {
        values.payLaterToYouDirectly.push(MONTHLY);
      }
      if (tier.payWeekly) {
        values.payLaterToYouDirectly.push(WEEKLY);
      }

      if (typeof tier.requireGuarantor == 'boolean') {
        values.requireGuarantor = tier.requireGuarantor ? YES : NO;
      }

      values.payNowAmount = tier.payNowAmount;
      values.tenancyUnit = tier.tenancyUnit;

      return values;
    });
  }, [tenancyInstalments, paymentOnline]);

  const handleTenancyLengthChange = ev => {
    const { value } = ev.target;

    resetFields();

    setTenancyLengthTiers(value);

    if (value === NO) {
      setTenancyInstalments(NO_TENANCY_TIERS);
    } else if (value === YES) {
      if (!instalments?.length || instalments.length === 1) {
        setTenancyInstalments(DEFAULT_TENANCY_TIERS);
      } else {
        setTenancyInstalments(instalments);
      }
    }
  };

  const handleSaveClick = () => {
    validateFields((err, values) => {
      if (!err) {
        const paymentOptionValue = getPaymentOptionsParams(values);

        const params = tenancyInstalments.map((tier, idx) => ({
          ...tier,
          ...paymentOptionValue[idx],
          name: undefined,
          createdAt: undefined,
          updatedAt: undefined,
          paymentPlanId: undefined,
        }));

        params?.forEach(param => {
          if (param?.payNowAmount === 0) {
            delete param.payNowAmount;
          }
          // 针对只有 fullpayment 为true ,tenancyInstalments的country为{name:...}错误参数情况需要删除,country的值只有string形式，没有object的形式
          if (param?.country?.name) {
            param['country'] = null;
          }
          // no payment :payRemainderAtOnce false
          if (!paymentOnline && param?.payRemainderAtOnce) {
            param.payRemainderAtOnce = false;
          }
        });

        updateRentPayment({
          variables: {
            input: {
              paymentPlanId,
              landlordId: landlord?.id,
              orderInstalments: params,
            },
          },
        });
      } else {
        console.error(err);
      }
    });
  };

  useEffect(() => {
    if (tenancyInstalments.length) {
      tenancyInstalments.forEach((tier, idx) => {
        if (getFieldValue(`installments[${idx}].payment`).includes(RENTAL_INSTALMENTS)) {
          let currentPayLaterValues = getFieldValue(`installments[${idx}].payLaterToYouDirectly`);

          if (tier.toTenancy <= 12 && idx !== tenancyInstalments.length - 1) {
            currentPayLaterValues = currentPayLaterValues.filter(item => item !== QUARTERLY);
          }

          if (tier.toTenancy <= 4 && idx !== tenancyInstalments.length - 1) {
            currentPayLaterValues = currentPayLaterValues.filter(item => item !== MONTHLY);
          }

          setFieldsValue({
            [`installments[${idx}].payLaterToYouDirectly`]: currentPayLaterValues,
          });
        }
      });
    }
  }, [tenancyInstalments]);

  const getPaymentTierMaxTenancy = (toTenancy, unit) => {
    if (unit === WEEKS) {
      return Number(toTenancy) - 1;
    } else {
      return (toTenancy * 7 - ((toTenancy * 7) % 15)) / 30;
    }
  };

  return (
    <div className={classes.modal}>
      <div className={classes.wrapper} ref={wrapperRef}>
        <div className={classes.header}>
          <h3 className={classes.title}>
            {t('landlord.payment.long_tail.rent_payment.setting_modal.title')}
          </h3>

          <CloseOutlined className={classes.closeButton} onClick={onClose} />
        </div>
        <div className={classes.body}>
          <Form layout={'vertical'}>
            <section className={classes.section}>
              <h4 className={classes.sectionTitle}>
                {t('landlord.payment.long_tail.rent_payment.setting_modal.tenancy_length_tiers')}
              </h4>
              <p className={classes.sectionSummary}>
                {t(
                  'landlord.payment.long_tail.rent_payment.setting_modal.tenancy_length_tiers.summary',
                )}
              </p>

              <div className={classes.tenancyLengthTiers}>
                <Form.Item
                  required={true}
                  label={t(
                    'landlord.payment.long_tail.rent_payment.setting_modal.tenancy_length_tiers.label',
                  )}
                >
                  <Radio.Group value={tenancyLengthTiers} onChange={handleTenancyLengthChange}>
                    <Radio value={YES}>
                      {t('landlord.payment.long_tail.rent_payment.setting_modal.yes')}
                    </Radio>
                    <Radio value={NO}>
                      {t('landlord.payment.long_tail.rent_payment.setting_modal.no')}
                    </Radio>
                  </Radio.Group>
                </Form.Item>

                <If condition={tenancyLengthTiers === YES}>
                  <Form.Item
                    required={true}
                    label={t(
                      'landlord.payment.long_tail.rent_payment.setting_modal.define_number_of_tiers.label',
                    )}
                    className={classes.formTierDefine}
                  >
                    <TierDefineControl
                      tenancyInstalments={tenancyInstalments}
                      setTenancyInstalments={setTenancyInstalments}
                      getTierRenderText={getTierRenderText}
                    />
                  </Form.Item>
                </If>
              </div>
            </section>

            <section className={classes.section}>
              <h4 className={classes.sectionTitle}>
                {t('landlord.payment.long_tail.rent_payment.setting_modal.rent_payment_options')}
              </h4>

              <Choose>
                <When condition={tenancyLengthTiers === YES}>
                  <For each="tier" index="index" of={tenancyInstalments}>
                    <div className={classes.rentPaymentOption}>
                      <div className="option--title">
                        <span className="option--title__index">{index + 1}</span> Tenancy Length{' '}
                        <span>{getSafeHtml(getTierRenderText(tier))}</span>
                      </div>

                      <Form.Item
                        label={t(
                          'landlord.payment.long_tail.rent_payment.setting_modal.rent_payment_options.label',
                        )}
                        className={classes.formRentPayment}
                      >
                        {getFieldDecorator(`installments[${index}].payment`, {
                          initialValue: paymentOptionInitialValues[index].payment,
                          rules: [
                            {
                              required: true,
                              message: t(
                                'landlord.payment.long_tail.rent_payment.setting_modal.empty.err',
                              ),
                            },
                          ],
                        })(
                          <Checkbox.Group>
                            <div className="option--checkbox">
                              <Checkbox value={FULL_PAYMENT} disabled={!paymentOnline}>
                                {t(
                                  'landlord.payment.long_tail.rent_payment.setting_modal.full_payment',
                                )}
                                <span className="explain">
                                  {paymentOnline
                                    ? t(
                                        'landlord.payment.long_tail.rent_payment.setting_modal.full_payment.explain',
                                      )
                                    : t(
                                        'landlord.payment.long_tail.rent_payment.setting_modal.full_payment.nopayment.explain',
                                      )}
                                </span>
                              </Checkbox>
                            </div>
                            <div className="option--checkbox">
                              <Checkbox value={RENTAL_INSTALMENTS}>
                                {t(
                                  'landlord.payment.long_tail.rent_payment.setting_modal.rental_payment',
                                )}
                              </Checkbox>
                            </div>
                          </Checkbox.Group>,
                        )}
                      </Form.Item>

                      <If
                        condition={getFieldValue(`installments[${index}].payment`)?.includes(
                          RENTAL_INSTALMENTS,
                        )}
                      >
                        <Row className="option--extra-form" type="flex" justify="start" gutter={40}>
                          <If condition={!isMustPayDespositFee && paymentOnline}>
                            <Col span={12} style={{ position: 'relative' }}>
                              <Form.Item
                                label={
                                  <span className="option--label">
                                    {t(
                                      'landlord.payment.long_tail.rent_payment.setting_modal.pay_now_to_student',
                                    )}
                                    <Tooltip
                                      title={t(
                                        'landlord.payment.long_tail.rent_payment.setting_modal.pay_now_to_student.tooltip',
                                      )}
                                      placement="top"
                                      overlayStyle={{ zIndex: '10000000' }}
                                    >
                                      <QuestionCircleOutlined
                                        style={{ color: '#38B2A6', marginLeft: 8 }}
                                      />
                                    </Tooltip>
                                  </span>
                                }
                              >
                                {' '}
                                <Row type="flex" justify="start" gutter={0}>
                                  <Col span={18}>
                                    {getFieldDecorator(`installments[${index}].payNowAmount`, {
                                      initialValue: paymentOptionInitialValues[index].payNowAmount,
                                      rules: [
                                        {
                                          required: true,
                                          message: t(
                                            'landlord.payment.long_tail.rent_payment.setting_modal.empty.err',
                                          ),
                                        },
                                        {
                                          type: 'number',
                                          max:
                                            index == tenancyInstalments.length - 1
                                              ? 99999
                                              : getPaymentTierMaxTenancy(
                                                  tier.toTenancy,
                                                  getFieldValue(
                                                    `installments[${index}].tenancyUnit`,
                                                  ),
                                                ),
                                          message:
                                            index == tenancyInstalments.length - 1
                                              ? t(
                                                  'landlord.payment.long_tail.rent_payment.setting_modal.pay_now_to_student.max_error_msg',
                                                )
                                              : t(
                                                  'landlord.payment.long_tail.rent_payment.setting_modal.pay_now_to_student.error_msg',
                                                ),
                                        },
                                      ],
                                    })(
                                      <InputNumber
                                        className="tenancy-unit-input"
                                        step={
                                          getFieldValue(`installments[${index}].tenancyUnit`) ===
                                          WEEKS
                                            ? 1
                                            : 0.5
                                        }
                                        min={
                                          getFieldValue(`installments[${index}].tenancyUnit`) ===
                                          WEEKS
                                            ? 1
                                            : 0.5
                                        }
                                        max={
                                          index == tenancyInstalments.length - 1
                                            ? 99999
                                            : getPaymentTierMaxTenancy(
                                                tier.toTenancy,
                                                getFieldValue(`installments[${index}].tenancyUnit`),
                                              )
                                        }
                                      />,
                                    )}
                                  </Col>

                                  <Col span={6}>
                                    {getFieldDecorator(`installments[${index}].tenancyUnit`, {
                                      initialValue:
                                        paymentOptionInitialValues[index].tenancyUnit ?? WEEKS,
                                    })(
                                      <Select
                                        getPopupContainer={() => wrapperRef.current}
                                        onChange={() => {
                                          resetFields([`installments[${index}].payNowAmount`]);
                                        }}
                                        className="tenancy-unit-select"
                                      >
                                        <Option key={WEEKS} value={WEEKS}>
                                          {t('landlord.booking.detail.value.billing_cycle.weekly')}
                                        </Option>
                                        <Option
                                          key={MONTHS}
                                          value={MONTHS}
                                          disabled={tier.toTenancy && tier.toTenancy <= 2}
                                        >
                                          {t('landlord.booking.detail.value.billing_cycle.monthly')}
                                        </Option>
                                      </Select>,
                                    )}
                                  </Col>
                                </Row>
                              </Form.Item>
                            </Col>
                          </If>
                          <Col
                            span={12}
                            className={classNames({
                              'option--extra-form__noPayment': !paymentOnline,
                            })}
                          >
                            <Form.Item
                              label={
                                <span className="option--label">
                                  {t(
                                    'landlord.payment.long_tail.rent_payment.setting_modal.pay_later_to_you_directly',
                                  )}
                                  <Tooltip
                                    title={
                                      paymentOnline
                                        ? t(
                                            'landlord.payment.long_tail.rent_payment.setting_modal.pay_later_to_you_directly.tooltip',
                                          )
                                        : t(
                                            'landlord.payment.long_tail.rent_payment.setting_modal.pay_later.nopayment.explain',
                                          )
                                    }
                                    placement="top"
                                    overlayStyle={{ zIndex: '10000000' }}
                                  >
                                    <QuestionCircleOutlined
                                      style={{ color: '#38B2A6', marginLeft: 8 }}
                                    />
                                  </Tooltip>
                                </span>
                              }
                              className="option--extra-form__item"
                            >
                              {getFieldDecorator(`installments[${index}].payLaterToYouDirectly`, {
                                initialValue:
                                  paymentOptionInitialValues[index].payLaterToYouDirectly,
                                rules: [
                                  {
                                    required: true,
                                    message: t(
                                      'landlord.payment.long_tail.rent_payment.setting_modal.empty.err',
                                    ),
                                  },
                                ],
                              })(
                                <Checkbox.Group>
                                  <If condition={paymentOnline}>
                                    <Checkbox value={ALL_AT_ONCE} disabled={true}>
                                      {t(
                                        'landlord.payment.long_tail.rent_payment.setting_modal.all_at_once',
                                      )}
                                    </Checkbox>
                                  </If>
                                  <Checkbox
                                    value={QUARTERLY}
                                    disabled={
                                      tier.toTenancy <= 12 &&
                                      index !== tenancyInstalments.length - 1
                                    }
                                  >
                                    {t(
                                      'landlord.payment.long_tail.rent_payment.setting_modal.quarterly',
                                    )}
                                  </Checkbox>
                                  <Checkbox
                                    value={MONTHLY}
                                    disabled={
                                      tier.toTenancy <= 4 && index !== tenancyInstalments.length - 1
                                    }
                                  >
                                    {t(
                                      'landlord.payment.long_tail.rent_payment.setting_modal.monthly',
                                    )}
                                  </Checkbox>
                                  <Checkbox value={WEEKLY}>
                                    {t(
                                      'landlord.payment.long_tail.rent_payment.setting_modal.weekly',
                                    )}
                                  </Checkbox>
                                </Checkbox.Group>,
                              )}
                            </Form.Item>
                          </Col>
                          <Col
                            span={24}
                            className={classNames({
                              'option--extra-form__noPayment': !paymentOnline,
                            })}
                          >
                            <Form.Item
                              label={t(
                                'landlord.payment.long_tail.rent_payment.setting_modal.guarantor_required',
                              )}
                              className={classNames('option--extra-form__item', {
                                'option--extra-form__noPayment': !paymentOnline,
                              })}
                            >
                              {getFieldDecorator(`installments[${index}].requireGuarantor`, {
                                initialValue: paymentOptionInitialValues[index].requireGuarantor,
                                rules: [
                                  {
                                    required: true,
                                    message: t(
                                      'landlord.payment.long_tail.rent_payment.setting_modal.empty.err',
                                    ),
                                  },
                                ],
                              })(
                                <Radio.Group>
                                  <Radio value={YES}>
                                    {t('landlord.payment.long_tail.rent_payment.setting_modal.yes')}
                                  </Radio>
                                  <Radio value={NO}>
                                    {t('landlord.payment.long_tail.rent_payment.setting_modal.no')}
                                  </Radio>
                                </Radio.Group>,
                              )}
                            </Form.Item>
                          </Col>

                          <If
                            condition={
                              getFieldsValue()?.installments[index]?.requireGuarantor === 'YES'
                            }
                          >
                            <Row
                              className="option--guarantorLocation"
                              type="flex"
                              justify="space-between"
                            >
                              <Col span={12}>
                                <Form.Item
                                  label={
                                    <span>
                                      {t(
                                        'landlord.payment.long_tail.rent_payment.setting_modal.guarantor_location',
                                      )}
                                      <Tooltip
                                        title={t(
                                          'landlord.payment.long_tail.rent_payment.setting_modal.guarantor_location.hint',
                                        )}
                                        placement="top"
                                        overlayStyle={{ zIndex: '10000000' }}
                                      >
                                        <QuestionCircleOutlined
                                          style={{ color: 'rgba(0, 0, 0, 0.45)', marginLeft: 4 }}
                                        />
                                      </Tooltip>
                                    </span>
                                  }
                                  className="option--extra-form__item"
                                >
                                  {getFieldDecorator(`installments[${index}].guarantorLocation`, {
                                    initialValue:
                                      instalments?.length > 0
                                        ? instalments?.[index]?.country === null
                                          ? 'All'
                                          : 'special'
                                        : 'All',
                                    rules: [
                                      {
                                        required: true,
                                        message: t(
                                          'landlord.payment.long_tail.rent_payment.setting_modal.empty.err',
                                        ),
                                      },
                                    ],
                                  })(
                                    <Radio.Group
                                      onChange={e => {
                                        if (e?.target?.value === 'All') {
                                          form.setFields({
                                            [`installments[${index}].guarantorCountry`]: {
                                              value: undefined,
                                            },
                                          });
                                        } else {
                                          resetFields([`installments[${index}].guarantorCountry`]);
                                        }
                                      }}
                                    >
                                      <Radio value="All">All</Radio>
                                      <Radio value="special">
                                        {t(
                                          'landlord.payment.long_tail.rent_payment.setting_modal.location_specific',
                                        )}
                                      </Radio>
                                    </Radio.Group>,
                                  )}
                                </Form.Item>
                              </Col>

                              <Col span={12} style={{ width: '344px' }}>
                                <Form.Item
                                  label={t(
                                    'landlord.payment.long_tail.rent_payment.setting_modal.country',
                                  )}
                                  style={{
                                    marginBottom: 0,
                                  }}
                                >
                                  {getFieldDecorator(`installments[${index}].guarantorCountry`, {
                                    initialValue:
                                      getFieldsValue()?.installments[index]?.guarantorLocation ===
                                      'All'
                                        ? undefined
                                        : instalments?.[index]?.country &&
                                          instalments?.[index]?.country !== null
                                        ? instalments?.[index]?.country?.name
                                        : defaultCountry
                                        ? defaultCountry
                                        : undefined,
                                    rules: [
                                      {
                                        validator: (rule, value, callback) => {
                                          if (
                                            getFieldValue(
                                              `installments[${index}].guarantorLocation`,
                                            ) === 'special' &&
                                            !value
                                          ) {
                                            callback(t('Please choose a specific country/region'));
                                          } else {
                                            callback();
                                          }
                                        },
                                      },
                                    ],
                                  })(
                                    <Select
                                      disabled={
                                        getFieldsValue()?.installments[index]?.guarantorLocation ===
                                        'All'
                                      }
                                      style={{ width: '100%' }}
                                      showSearch
                                      placeholder={t(
                                        'landlord.payment.long_tail.rent_payment.setting_modal.ploceholder',
                                      )}
                                      showArrow={false}
                                      filterOption={false}
                                      onSearch={onSearch}
                                      getPopupContainer={() => wrapperRef.current}
                                      notFoundContent={null}
                                    >
                                      {chooseCountries?.map(country => (
                                        <Option key={country}>{country}</Option>
                                      ))}
                                    </Select>,
                                  )}
                                </Form.Item>
                              </Col>
                            </Row>{' '}
                          </If>
                        </Row>
                      </If>
                    </div>
                  </For>
                </When>
                <Otherwise condition={tenancyLengthTiers === NO}>
                  <div className={classes.rentPaymentOption}>
                    <Form.Item
                      label={t(
                        'landlord.payment.long_tail.rent_payment.setting_modal.rent_payment_options.label',
                      )}
                      className={classes.formRentPayment}
                    >
                      {getFieldDecorator(`installments[0].payment`, {
                        initialValue: paymentOptionInitialValues[0].payment,
                        rules: [
                          {
                            required: true,
                            message: t(
                              'landlord.payment.long_tail.rent_payment.setting_modal.empty.err',
                            ),
                          },
                        ],
                      })(
                        <Checkbox.Group>
                          <div className="option--checkbox">
                            <Checkbox value={FULL_PAYMENT} disabled={!paymentOnline}>
                              {t(
                                'landlord.payment.long_tail.rent_payment.setting_modal.full_payment',
                              )}

                              <span className="explain">
                                {paymentOnline
                                  ? t(
                                      'landlord.payment.long_tail.rent_payment.setting_modal.full_payment.explain',
                                    )
                                  : t(
                                      'landlord.payment.long_tail.rent_payment.setting_modal.full_payment.nopayment.explain',
                                    )}
                              </span>
                            </Checkbox>
                          </div>
                          <div className="option--checkbox">
                            <Checkbox value={RENTAL_INSTALMENTS}>
                              {t(
                                'landlord.payment.long_tail.rent_payment.setting_modal.rental_payment',
                              )}
                            </Checkbox>
                          </div>
                        </Checkbox.Group>,
                      )}
                    </Form.Item>

                    <If
                      condition={getFieldValue(`installments[0].payment`)?.includes(
                        RENTAL_INSTALMENTS,
                      )}
                    >
                      <Row className="option--extra-form" type="flex" justify="start" gutter={40}>
                        <If condition={!isMustPayDespositFee && paymentOnline}>
                          <Col span={12} style={{ position: 'relative' }}>
                            <Form.Item
                              label={
                                <span className="option--label">
                                  {t(
                                    'landlord.payment.long_tail.rent_payment.setting_modal.pay_now_to_student',
                                  )}
                                  <Tooltip
                                    title={t(
                                      'landlord.payment.long_tail.rent_payment.setting_modal.pay_now_to_student.tooltip',
                                    )}
                                    placement="top"
                                    overlayStyle={{ zIndex: '10000000' }}
                                  >
                                    <QuestionCircleOutlined
                                      style={{ color: '#38B2A6', marginLeft: 8 }}
                                    />
                                  </Tooltip>
                                </span>
                              }
                              className="option--extra-form__item"
                            >
                              <Row type="flex" justify="start" gutter={0}>
                                <Col span={18}>
                                  {getFieldDecorator(`installments[0].payNowAmount`, {
                                    initialValue: paymentOptionInitialValues[0].payNowAmount,
                                    rules: [
                                      {
                                        required: true,
                                        message: t(
                                          'landlord.payment.long_tail.rent_payment.setting_modal.empty.err',
                                        ),
                                      },
                                      {
                                        type: 'number',
                                        max: 99999,
                                        message: t(
                                          'landlord.payment.long_tail.rent_payment.setting_modal.pay_now_to_student.max_error_msg',
                                        ),
                                      },
                                    ],
                                  })(
                                    <InputNumber
                                      className="tenancy-unit-input"
                                      step={
                                        getFieldValue('installments[0].tenancyUnit') === WEEKS
                                          ? 1
                                          : 0.5
                                      }
                                      min={
                                        getFieldValue('installments[0].tenancyUnit') === WEEKS
                                          ? 1
                                          : 0.5
                                      }
                                    />,
                                  )}
                                </Col>
                                <Col span={6}>
                                  {getFieldDecorator(`installments[0].tenancyUnit`, {
                                    initialValue:
                                      paymentOptionInitialValues[0].tenancyUnit ?? WEEKS,
                                  })(
                                    <Select
                                      className="tenancy-unit-select"
                                      getPopupContainer={() => wrapperRef.current}
                                      onChange={() => {
                                        resetFields([`installments[0].payNowAmount`]);
                                      }}
                                    >
                                      <Option key={WEEKS} value={WEEKS}>
                                        {t('landlord.booking.detail.value.billing_cycle.weekly')}
                                      </Option>
                                      <Option key={MONTHS} value={MONTHS}>
                                        {t('landlord.booking.detail.value.billing_cycle.monthly')}
                                      </Option>
                                    </Select>,
                                  )}
                                </Col>
                              </Row>
                            </Form.Item>
                          </Col>
                        </If>
                        <Col
                          span={12}
                          className={classNames({
                            'option--extra-form__noPayment': !paymentOnline,
                          })}
                        >
                          <Form.Item
                            label={
                              <span className="option--label">
                                {t(
                                  'landlord.payment.long_tail.rent_payment.setting_modal.pay_later_to_you_directly',
                                )}
                                <Tooltip
                                  title={
                                    paymentOnline
                                      ? t(
                                          'landlord.payment.long_tail.rent_payment.setting_modal.pay_later_to_you_directly.tooltip',
                                        )
                                      : t(
                                          'landlord.payment.long_tail.rent_payment.setting_modal.pay_later.nopayment.explain',
                                        )
                                  }
                                  placement="top"
                                  overlayStyle={{ zIndex: '10000000' }}
                                >
                                  <QuestionCircleOutlined
                                    style={{ color: '#38B2A6', marginLeft: 8 }}
                                  />
                                </Tooltip>
                              </span>
                            }
                            className="option--extra-form__item"
                          >
                            {getFieldDecorator(`installments[0].payLaterToYouDirectly`, {
                              initialValue: paymentOptionInitialValues[0].payLaterToYouDirectly,
                              rules: [
                                {
                                  required: true,
                                  message: t(
                                    'landlord.payment.long_tail.rent_payment.setting_modal.empty.err',
                                  ),
                                },
                              ],
                            })(
                              <Checkbox.Group>
                                <If condition={paymentOnline}>
                                  <Checkbox value={ALL_AT_ONCE} disabled={true}>
                                    {t(
                                      'landlord.payment.long_tail.rent_payment.setting_modal.all_at_once',
                                    )}
                                  </Checkbox>
                                </If>
                                <Checkbox value={QUARTERLY}>
                                  {t(
                                    'landlord.payment.long_tail.rent_payment.setting_modal.quarterly',
                                  )}
                                </Checkbox>
                                <Checkbox value={MONTHLY}>
                                  {t(
                                    'landlord.payment.long_tail.rent_payment.setting_modal.monthly',
                                  )}
                                </Checkbox>
                                <Checkbox value={WEEKLY}>
                                  {t(
                                    'landlord.payment.long_tail.rent_payment.setting_modal.weekly',
                                  )}
                                </Checkbox>
                              </Checkbox.Group>,
                            )}
                          </Form.Item>
                        </Col>
                        <Col
                          span={24}
                          className={classNames({
                            'option--extra-form__noPayment': !paymentOnline,
                          })}
                        >
                          <Form.Item
                            label={t(
                              'landlord.payment.long_tail.rent_payment.setting_modal.guarantor_required',
                            )}
                            className="option--extra-form__item"
                          >
                            {getFieldDecorator(`installments[0].requireGuarantor`, {
                              initialValue: paymentOptionInitialValues[0].requireGuarantor,
                              rules: [
                                {
                                  required: true,
                                  message: t(
                                    'landlord.payment.long_tail.rent_payment.setting_modal.empty.err',
                                  ),
                                },
                              ],
                            })(
                              <Radio.Group>
                                <Radio value={YES}>
                                  {t('landlord.payment.long_tail.rent_payment.setting_modal.yes')}
                                </Radio>
                                <Radio value={NO}>
                                  {t('landlord.payment.long_tail.rent_payment.setting_modal.no')}
                                </Radio>
                              </Radio.Group>,
                            )}
                          </Form.Item>
                        </Col>
                        <If
                          condition={getFieldsValue()?.installments[0]?.requireGuarantor === 'YES'}
                        >
                          <Row
                            className="option--guarantorLocation"
                            type="flex"
                            justify="space-between"
                          >
                            <Col span={12}>
                              <Form.Item
                                label={
                                  <span>
                                    {t(
                                      'landlord.payment.long_tail.rent_payment.setting_modal.guarantor_location',
                                    )}
                                    <Tooltip
                                      title={t(
                                        'landlord.payment.long_tail.rent_payment.setting_modal.guarantor_location.hint',
                                      )}
                                      placement="top"
                                      overlayStyle={{ zIndex: '10000000' }}
                                    >
                                      <QuestionCircleOutlined
                                        style={{ color: 'rgba(0, 0, 0, 0.45)', marginLeft: 4 }}
                                      />
                                    </Tooltip>
                                  </span>
                                }
                                className="option--extra-form__item"
                              >
                                {getFieldDecorator(`installments[0].guarantorLocation`, {
                                  initialValue:
                                    instalments?.length > 0
                                      ? instalments?.[0]?.country === null
                                        ? 'All'
                                        : 'special'
                                      : 'All',
                                  rules: [
                                    {
                                      required: true,
                                      message: t(
                                        'landlord.payment.long_tail.rent_payment.setting_modal.empty.err',
                                      ),
                                    },
                                  ],
                                })(
                                  <Radio.Group
                                    onChange={e => {
                                      if (e?.target?.value === 'All') {
                                        form.setFields({
                                          [`installments[0].guarantorCountry`]: {
                                            value: undefined,
                                          },
                                        });
                                      } else {
                                        resetFields([`installments[0].guarantorCountry`]);
                                      }
                                    }}
                                  >
                                    <Radio value="All">All</Radio>
                                    <Radio value="special">
                                      {t(
                                        'landlord.payment.long_tail.rent_payment.setting_modal.location_specific',
                                      )}
                                    </Radio>
                                  </Radio.Group>,
                                )}
                              </Form.Item>
                            </Col>
                            <Col span={12} style={{ width: '344px' }}>
                              <Form.Item
                                label={t(
                                  'landlord.payment.long_tail.rent_payment.setting_modal.country',
                                )}
                                style={{
                                  marginBottom: 0,
                                }}
                              >
                                {getFieldDecorator(`installments[0].guarantorCountry`, {
                                  initialValue:
                                    getFieldsValue()?.installments[0]?.guarantorLocation === 'All'
                                      ? undefined
                                      : instalments?.[0]?.country &&
                                        instalments?.[0]?.country !== null
                                      ? instalments?.[0]?.country?.name
                                      : defaultCountry
                                      ? defaultCountry
                                      : undefined,
                                  rules: [
                                    {
                                      validator: (rule, value, callback) => {
                                        if (
                                          getFieldValue(`installments[0].guarantorLocation`) ===
                                            'special' &&
                                          !value
                                        ) {
                                          callback(t('Please choose a specific country/region'));
                                        } else {
                                          callback();
                                        }
                                      },
                                    },
                                  ],
                                })(
                                  <Select
                                    disabled={
                                      getFieldsValue()?.installments[0]?.guarantorLocation === 'All'
                                    }
                                    style={{ width: '100%' }}
                                    showSearch
                                    placeholder={t(
                                      'landlord.payment.long_tail.rent_payment.setting_modal.ploceholder',
                                    )}
                                    showArrow={false}
                                    filterOption={false}
                                    onSearch={onSearch}
                                    getPopupContainer={() => wrapperRef.current}
                                    notFoundContent={null}
                                  >
                                    {chooseCountries?.map(country => (
                                      <Option key={country}>{country}</Option>
                                    ))}
                                  </Select>,
                                )}
                              </Form.Item>
                            </Col>
                          </Row>{' '}
                        </If>
                      </Row>
                    </If>
                  </div>
                </Otherwise>
              </Choose>
            </section>
          </Form>
        </div>
        <div className={classes.footer}>
          <Button className={classes.cancelButton} onClick={onClose}>
            {t('landlord.payment.long_tail.button.cancel')}
          </Button>
          <Button
            className={classes.saveButton}
            type="primary"
            loading={loading}
            onClick={handleSaveClick}
          >
            {t('landlord.payment.long_tail.button.save')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Form.create({ name: 'RentPaymentSettingModal' })(
  modal({
    className: 'rent-payment-setting-modal',
  })(RentPaymentSettingModal),
);
